"use client"

import * as React from 'react'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm, type CrudField, type CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'
import { apiCall } from '@open-mercato/ui/backend/utils/apiCall'
import { raiseCrudError } from '@open-mercato/ui/backend/utils/serverErrors'
import { useT } from '@open-mercato/shared/lib/i18n/context'

export default function SignDocumentPage({ params }: { params?: { id?: string } }) {
  const t = useT()
  const id = params?.id

  const fields = React.useMemo<CrudField[]>(() => [
    {
      id: 'role',
      label: t('documents.form.fields.status', 'Role'),
      type: 'select',
      required: true,
      options: [
        { value: 'SENDER', label: 'Sender' },
        { value: 'CARRIER', label: 'Carrier' },
        { value: 'RECEIVER', label: 'Receiver' },
        { value: 'INSPECTOR', label: 'Inspector' },
      ],
    },
    {
      id: 'signerName',
      label: t('documents.actions.sign', 'Signer Name'),
      type: 'text',
      required: true,
    },
    {
      id: 'method',
      label: t('documents.form.fields.documentType', 'Method'),
      type: 'select',
      required: true,
      options: [
        { value: 'MANUAL', label: 'Manual' },
        { value: 'ESEAL', label: 'E-Seal' },
        { value: 'QUALIFIED', label: 'Qualified' },
        { value: 'HANDWRITTEN_SCAN', label: 'Handwritten Scan' },
      ],
    },
    {
      id: 'signatureRef',
      label: t('documents.form.fields.notes', 'Signature Reference'),
      type: 'text',
    },
  ], [t])

  const groups = React.useMemo<CrudFormGroup[]>(() => [
    {
      id: 'sign',
      title: t('documents.actions.sign', 'Sign'),
      column: 1,
      fields: ['role', 'signerName', 'method', 'signatureRef'],
    },
  ], [t])

  const successRedirect = React.useMemo(
    () => id
      ? `/backend/documents/${encodeURIComponent(id)}?flash=${encodeURIComponent(t('documents.messages.signSuccess', 'Document signed successfully'))}&type=success`
      : '/backend/documents',
    [id, t],
  )

  if (!id) {
    return null
  }

  return (
    <Page>
      <PageBody>
        <CrudForm
          title={t('documents.actions.sign', 'Sign Document')}
          backHref={`/backend/documents/${encodeURIComponent(id)}`}
          fields={fields}
          groups={groups}
          initialValues={{ method: 'MANUAL' }}
          submitLabel={t('documents.actions.sign', 'Sign')}
          cancelHref={`/backend/documents/${encodeURIComponent(id)}`}
          successRedirect={successRedirect}
          onSubmit={async (values) => {
            const call = await apiCall(`/api/freight/documents/${encodeURIComponent(id)}/sign`, {
              method: 'POST',
              body: JSON.stringify(values),
            })
            if (!call.ok) {
              await raiseCrudError(call.response, t('documents.messages.signError', 'Failed to sign document'))
            }
          }}
        />
      </PageBody>
    </Page>
  )
}
